import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, PhoneCall, Users, CheckSquare, Mail,
  MoreHorizontal, X, Phone, Star, FileText,
  Clapperboard, TrendingUp, Lightbulb, Building2,
  DollarSign, Settings, Calendar, LogOut, Bot, HardDrive,
  Search, Flame, BarChart3, Send, Bug, Handshake, Wrench,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { useI18n } from '../../contexts/I18nContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useMessagesUnread } from '../../contexts/MessagesUnreadContext'
import { useJanetQueueCount } from '../../hooks/useJanetQueue'
import { BLOCKS, BLOCK_NAV, BLOCK_ORDER } from '../../config/themes'
import QuickLogModal from '../calllog/QuickLogModal'
import DialerModal from '../calllog/DialerModal'
import BugReportModal from '../bugReport/BugReportModal'
import SuggestionModal from '../bugReport/SuggestionModal'
import OfflineStatusBanner from './OfflineStatusBanner'
import PaneErrorBoundary from '../PaneErrorBoundary'
import BlockTabBar from './BlockTabBar'
import JanetReturnBridge from './JanetReturnBridge'
import DemoRail from '../demo/DemoRail'
import ShellFrame from '../shell/ShellFrame'
import useShellPreview from '../shell/shellPreview'

// Bottom nav = the color Block rail (horizontal mirror of the desktop sidebar).
// Tapping a Block opens its landing page; its sections show in the top BlockTabBar.
// "More" drawer — grouped by Block to match the desktop sidebar IA.
// Home / Inbox / Contacts / Tasks live in the bottom bar, so they're omitted here.
const MORE_GROUPS = [
  { block: 'universal', items: [
    { icon: Users,     label: 'Contacts',    path: '/contacts' },
    { icon: CheckSquare, label: 'Tasks',     path: '/tasks' },
    { icon: Bot,       label: 'Janet',       path: '/janet/queue' },
    { icon: PhoneCall, label: 'Comms',       path: '/comms' },
    { icon: Calendar,  label: 'Calendar',    path: '/calendar' },
    { icon: Bot,       label: 'Janet Queue', path: '/janet/queue' },
    { icon: Phone,     label: 'Activity',    path: '/activity' },
  ]},
  { block: 'management', items: [
    { icon: Star,      label: 'Clients',       path: '/clients' },
    { icon: FileText,  label: 'Properties',    path: '/properties' },
    { icon: TrendingUp,label: 'Deals',         path: '/deals?block=management' },
    { icon: Send,      label: 'Submissions',   path: '/submissions?view=management' },
    { icon: Lightbulb, label: 'Opportunities', path: '/opportunities?view=management' },
  ]},
  { block: 'production', items: [
    { icon: Clapperboard, label: 'Versions',   path: '/projects' },
    { icon: FileText,     label: 'Properties',    path: '/properties?block=production' },
    { icon: TrendingUp,   label: 'Deals',         path: '/deals?block=production' },
    { icon: Lightbulb,    label: 'Opportunities', path: '/opportunities?view=production' },
    { icon: Send,         label: 'Submissions',   path: '/submissions?view=production' },
  ]},
  { block: 'finance', items: [
    { icon: DollarSign, label: 'Finance', path: '/finance' },
    { icon: TrendingUp, label: 'Deals',   path: '/deals?block=finance' },
    { icon: BarChart3,  label: 'Reports', path: '/reports' },
  ]},
  { block: 'directory', items: [
    { icon: Building2, label: 'Companies', path: '/companies' },
  ]},
  { block: 'research', items: [
    { icon: Search, label: 'Research', path: '/research' },
    { icon: Flame,  label: 'Audience', path: '/audience' },
  ]},
  { block: 'engineering', items: [
    { icon: Wrench, label: 'Engineering', path: '/engineering' },
  ]},
  { block: 'system', items: [
    { icon: HardDrive, label: 'Files',    path: '/files' },
    { icon: Settings,  label: 'Settings', path: '/settings' },
  ]},
]

export default function MobileLayout() {
  const { t } = useI18n()
  const [showMore, setShowMore] = useState(false)
  const [showDialer, setShowDialer] = useState(false)
  const [showLog, setShowLog] = useState(false)
  const [showBug, setShowBug] = useState(false)
  const [showSuggest, setShowSuggest] = useState(false)
  const [logContactId, setLogContactId] = useState(null)
  const { user, profile, signOut } = useAuth()
  const { block: activeBlock } = useTheme()
  const { unread: messagesUnread } = useMessagesUnread()
  const { pending: janetPending } = useJanetQueueCount({ userId: user?.id })
  const shellPreview = useShellPreview()
  const navigate = useNavigate()
  const location = useLocation()
  const [inboxBadge, setInboxBadge] = useState(0)

  // Inbox unread badge — the viewer's own mailbox (the Inbox page filters the same way)
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('email_intelligence')
      .select('id', { count: 'estimated', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .in('status', ['analyzed', 'reviewed'])
      .then(({ count }) => setInboxBadge(count || 0))
      .catch(() => {})
  }, [location.pathname, user?.id])

  // Chat owns its scroll region and composer; fit the remaining shell height.
  // Its own Janet header also replaces the redundant compact Janet rail.
  const isJanetChat = /^\/janet\/chat(?:\/|$)/.test(location.pathname)
  const isOnInbox = location.pathname === '/inbox'

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U'

  const handleMoreNav = (path) => {
    setShowMore(false)
    navigate(path)
  }

  return (
    <div
      className="ns-shell h-[100dvh] flex flex-col overflow-hidden"
      style={{
        // border-box: this padding shrinks the content area rather than
        // overflowing 100dvh, so the top row clears the notch / status bar.
        paddingTop: 'env(safe-area-inset-top)',
        // One shell family (Responsive Amendment §D.1): paper ground, no Blocks branch.
        background: 'var(--ns-paper)',
      }}
    >
      <OfflineStatusBanner />
      {/* The trailer's guided rail — a phone-width visitor gets the same six scenes. Nothing outside demo mode. */}
      <DemoRail />
      {/* Eleventh Gate H.3 — contextual module subnav only: the Universal block's tab
          row would duplicate global navigation on Home/Inbox, so it renders nothing
          there; entering a module shows that module's sections. */}
      {activeBlock !== 'universal' && <BlockTabBar />}
      {/* Janet's persistent object path (issue #169 Slice 6A) — mobile is a
          first-class certification surface for this bridge, not an
          afterthought. */}
      <JanetReturnBridge />
      {/* Page content — flex-1 so nav is always pinned at bottom */}
      {/* overflow-x-hidden is load-bearing (Responsive Amendment: the page body
          must never scroll horizontally): overflow-y-auto alone computes
          overflow-x to auto, so any row a device inflates past the viewport
          (iOS larger-text settings) turned the WHOLE page into a horizontal
          scroller — clipping the detail's bottom action bar and the review
          packet's verdict buttons off-screen. Seen on Aaron's iPhone 2026-08-25. */}
      <main className={`flex-1 min-h-0 overflow-x-hidden ${isJanetChat ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        <div className={isJanetChat ? 'h-full min-h-0' : 'pb-6'}>
          {shellPreview && !isJanetChat ? (
            <ShellFrame mobile>
              <PaneErrorBoundary label="this page" resetKeys={[location.pathname]}>
                <Outlet context={{ isMobile: true, toggleSidebar: () => {} }} />
              </PaneErrorBoundary>
            </ShellFrame>
          ) : (
            <PaneErrorBoundary label="this page" resetKeys={[location.pathname]}>
              <Outlet context={{ isMobile: true, toggleSidebar: () => {} }} />
            </PaneErrorBoundary>
          )}
        </div>
      </main>

      {/* Bottom nav — color Block rail (horizontal mirror of the desktop sidebar) */}
      <nav data-shell="mobile-nav" className="shrink-0 bg-white border-t border-warm-200 shadow-[0_-1px_8px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div
          className="flex items-stretch gap-0.5 px-1 pt-1.5"
          style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}
        >
          {[
            { key: 'home',  label: 'Home',  path: '/',           icon: LayoutDashboard, match: (p) => p === '/' },
            { key: 'inbox', label: 'Inbox', path: '/inbox',      icon: Mail,            match: (p) => p.startsWith('/inbox') },
            { key: 'slate', label: 'Slate', path: '/studio/hub', icon: Clapperboard,    match: (p) => p.startsWith('/studio') || p.startsWith('/projects') || p.startsWith('/properties') },
            { key: 'deals', label: 'Deals', path: '/deals',      icon: Handshake,       match: (p) => p.startsWith('/deals') },
          ].map(d => {
            const active = d.match(location.pathname)
            const Icon = d.icon
            return (
              <button
                key={d.key}
                data-active={active || undefined}
                onClick={() => navigate(d.path)}
                className="flex-1 min-w-0 flex flex-col items-center gap-1"
                style={{ boxShadow: active ? 'inset 0 var(--ns-rule-current) 0 var(--ns-ink)' : 'none', paddingTop: 4 }}
              >
                <span
                  className="relative w-full max-w-[36px] aspect-square rounded-lg flex items-center justify-center transition-colors"
                  style={{ color: active ? 'var(--ns-ink)' : 'var(--ns-icon-idle)' }}
                >
                  <Icon size={17} />
                  {d.key === 'inbox' && inboxBadge > 0 && (
                    <span
                      className="absolute -top-1 -right-1 text-[8px] font-bold min-w-[15px] h-[15px] px-0.5 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--ns-attention)', color: 'var(--ns-raise)' }}
                    >
                      {inboxBadge > 99 ? '99+' : inboxBadge}
                    </span>
                  )}
                </span>
                <span
                  className="text-[8.5px] leading-none truncate max-w-[46px]"
                  style={{ color: active ? 'var(--ns-ink)' : 'var(--ns-gray)', fontWeight: active ? 600 : 500 }}
                >
                  {d.label}
                </span>
              </button>
            )
          })}

          {/* More — overflow (Janet Queue, Activity) + full index */}
          <button
            onClick={() => setShowMore(true)}
            className="flex-1 min-w-0 flex flex-col items-center gap-1"
          >
            <span className="w-full max-w-[36px] aspect-square rounded-lg flex items-center justify-center" style={{ color: 'var(--ns-icon-idle)' }}>
              <MoreHorizontal size={17} />
            </span>
            <span className="text-[8.5px] font-medium leading-none" style={{ color: 'var(--ns-gray)' }}>{t('nav.more')}</span>
          </button>
        </div>
      </nav>

      {/* "More" slide-up drawer */}
      {showMore && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-50"
            onClick={() => setShowMore(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl max-h-[80vh] overflow-y-auto" style={{ background: 'var(--ns-paper)', borderTop: '1px solid var(--ns-hair)' }}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full" style={{ background: 'var(--ns-hair)' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3" style={{ borderBottom: '1px solid var(--ns-hair-soft)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--ns-ink)' }}>{t('nav.allModules')}</p>
              <button onClick={() => setShowMore(false)} className="p-1.5 rounded-lg hover:bg-warm-100 text-warm-400">
                <X size={18} />
              </button>
            </div>

            {/* Nav items — grouped by Block */}
            <div className="p-4 space-y-4">
              {MORE_GROUPS.map(group => {
                const blk = BLOCKS[group.block] || BLOCKS.default
                return (
                  <div key={group.block}>
                    <p
                      className="uppercase px-1 mb-1.5"
                      style={{ fontSize: 'var(--ns-caps-label-size)', letterSpacing: 'var(--ns-caps-label-track)', fontWeight: 600, color: 'var(--ns-faint)' }}
                    >
                      {blk.label}
                    </p>
                    <div className="grid grid-cols-3 gap-1">
                      {group.items.map(item => (
                        <button
                          key={`${group.block}-${item.path}`}
                          onClick={() => handleMoreNav(item.path)}
                          className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl active:opacity-80 transition-opacity"
                        >
                          <div
                            className="w-12 h-12 flex items-center justify-center"
                            style={{ background: 'var(--ns-raise)', border: '1px solid var(--ns-hair-soft)', borderRadius: 'var(--ns-r-field)' }}
                          >
                            <item.icon size={22} style={{ color: 'var(--ns-gray)' }} />
                          </div>
                          <span className="text-[11px] font-medium text-center leading-tight" style={{ color: 'var(--ns-ink-2)' }}>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Feedback — low-frequency utility, off the floating layer */}
            <div className="px-4 pb-1">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  onClick={() => { setShowMore(false); setShowDialer(true) }}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-warm-50 border border-warm-200 text-warm-600 active:opacity-80"
                >
                  <Phone size={15} style={{ color: 'var(--ns-gray)' }} />
                  <span className="text-xs font-medium">Call a contact</span>
                </button>
                <button
                  onClick={() => { setShowMore(false); navigate('/inbox?compose=true') }}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-warm-50 border border-warm-200 text-warm-600 active:opacity-80"
                >
                  <Mail size={15} style={{ color: 'var(--ns-gray)' }} />
                  <span className="text-xs font-medium">Compose</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setShowMore(false); setShowSuggest(true) }}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-warm-50 border border-warm-200 text-warm-600 active:opacity-80"
                >
                  <Lightbulb size={15} className="text-amber-500" />
                  <span className="text-xs font-medium">Suggest an idea</span>
                </button>
                <button
                  onClick={() => { setShowMore(false); setShowBug(true) }}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-warm-50 border border-warm-200 text-warm-600 active:opacity-80"
                >
                  <Bug size={15} className="text-terracotta-500" />
                  <span className="text-xs font-medium">Report a bug</span>
                </button>
              </div>
            </div>

            {/* User + sign out */}
            <div className="mx-4 mb-6 mt-2 p-3 rounded-xl bg-warm-50 border border-warm-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-navy-50 border border-navy-100 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-navy-500">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-warm-800 truncate">{profile?.full_name || user?.email}</p>
                <p className="text-[10px] text-warm-500 font-mono">{profile?.company_id || 'chatrone_us'}</p>
              </div>
              <button
                onClick={signOut}
                className="p-2 rounded-lg hover:bg-warm-200 text-warm-500 transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Eleventh Gate §H.2 — no permanent content-blocking circles on phone:
          Janet is reachable through the page header ask affordance and More;
          calling is contextual (Contacts/Comms/detail) and in More. */}
      {showBug && <BugReportModal isOpen={showBug} onClose={() => setShowBug(false)} />}
      {showSuggest && <SuggestionModal isOpen={showSuggest} onClose={() => setShowSuggest(false)} />}

      {/* Dialer — RingCentral rings this phone then connects the contact, and
          rc-dial logs it server-side. QuickLog only opens for the native-dialer
          fallback, which leaves no log row of its own. */}
      <DialerModal
        isOpen={showDialer}
        onClose={() => setShowDialer(false)}
        onDialComplete={(contactId) => {
          setLogContactId(contactId)
          setShowLog(true)
        }}
      />

      {/* Quick Log — opens after returning from native dialer */}
      <QuickLogModal
        isOpen={showLog}
        onClose={() => { setShowLog(false); setLogContactId(null) }}
        defaultContactId={logContactId}
      />
    </div>
  )
}
